import axios, { AxiosResponse } from 'axios';
import { ApiResponse, User, RegisterData } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002/api';

// Session auth now lives in an httpOnly cookie (see AuthContext.tsx / axiosInterceptor.ts)
// instead of a manually-attached Authorization header — every request below relies on
// axios.defaults.withCredentials (set in index.tsx) to send/receive it automatically.
class AuthService {
  private baseURL: string;

  constructor() {
    this.baseURL = `${API_BASE_URL}/auth`;
  }

  async login(email: string, password: string): Promise<ApiResponse<{ user: User }>> {
    try {
      const response: AxiosResponse<ApiResponse<{ user: User }>> = await axios.post(
        `${this.baseURL}/login`,
        { email, password },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 10000
        }
      );

      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }

      return {
        success: false,
        error: error.message || 'Login failed'
      };
    }
  }

  async register(userData: RegisterData): Promise<ApiResponse<{ user: User }>> {
    try {
      const response: AxiosResponse<ApiResponse<{ user: User }>> = await axios.post(
        `${this.baseURL}/register`,
        userData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 10000
        }
      );

      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }

      return {
        success: false,
        error: error.message || 'Registration failed'
      };
    }
  }

  async googleLogin(idToken: string): Promise<ApiResponse<{ user: User }>> {
    const response: AxiosResponse<ApiResponse<{ user: User }>> = await axios.post(
      `${this.baseURL}/google`,
      { idToken }
    );
    return response.data;
  }

  async logout(): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await axios.post(
        `${this.baseURL}/logout`,
        {},
        { timeout: 5000 }
      );

      return response.data;
    } catch (error: any) {
      // Even if logout fails on server, we consider it successful locally
      console.warn('Logout warning:', error.message);
      return { success: true };
    }
  }

  // Confirms the session cookie is still valid — used on app load instead of trusting
  // a cached localStorage flag (which previously assumed a stored token was still valid
  // with no live check at all).
  async verifySession(): Promise<boolean> {
    try {
      const response: AxiosResponse<ApiResponse<{ valid: boolean }>> = await axios.get(
        `${this.baseURL}/verify`,
        { timeout: 5000 }
      );

      return response.data.success && response.data.data?.valid === true;
    } catch (error) {
      return false;
    }
  }

  async getProfile(): Promise<ApiResponse<User>> {
    try {
      const response: AxiosResponse<ApiResponse<User>> = await axios.get(
        `${this.baseURL}/profile`,
        { timeout: 10000 }
      );

      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }

      return {
        success: false,
        error: error.message || 'Failed to get profile'
      };
    }
  }

  async updateProfile(userData: Partial<User>): Promise<ApiResponse<User>> {
    try {
      const response: AxiosResponse<ApiResponse<User>> = await axios.put(
        `${this.baseURL}/profile`,
        userData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 10000
        }
      );

      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }

      return {
        success: false,
        error: error.message || 'Failed to update profile'
      };
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await axios.put(
        `${this.baseURL}/change-password`,
        {
          currentPassword,
          newPassword
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 10000
        }
      );

      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }

      return {
        success: false,
        error: error.message || 'Failed to change password'
      };
    }
  }

  async forgotPassword(email: string): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await axios.post(
        `${this.baseURL}/forgot-password`,
        { email },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 10000
        }
      );

      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }

      return {
        success: false,
        error: error.message || 'Failed to send reset email'
      };
    }
  }

  async resetPassword(token: string, newPassword: string): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await axios.post(
        `${this.baseURL}/reset-password`,
        {
          token,
          newPassword
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 10000
        }
      );

      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }

      return {
        success: false,
        error: error.message || 'Failed to reset password'
      };
    }
  }

  async refreshToken(): Promise<ApiResponse> {
    try {
      // Backend re-issues the auth cookie on this call — nothing to store locally.
      const response: AxiosResponse<ApiResponse> = await axios.post(
        `${this.baseURL}/refresh`,
        {},
        { timeout: 10000 }
      );

      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }

      return {
        success: false,
        error: error.message || 'Failed to refresh token'
      };
    }
  }

  // Get current user from the local UI cache (non-authoritative — see AuthContext's
  // verifySession() call on app load for the actual auth check).
  getCurrentUser(): User | null {
    try {
      const userStr = localStorage.getItem('user');

      if (userStr) {
        return JSON.parse(userStr);
      }
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
    }

    return null;
  }
}

export const authService = new AuthService();
export default authService;
